import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { allPeopleSearchActions, selectAdHocSearch } from '@actions/search';
import ResultsDisplay from '@views/Search/ResultsDisplay';
import { useAppContactSelecting } from '@hooks/use-app-contact-selecting';
import KeyboardShortcut from '@components/KeyboardShortcut';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScrollRestoration } from '@hooks/use-scroll-restoration';
import { ViewID } from '@reducers/ui/viewRestoration';
import { useSaveViewState } from '@support/hooks/use-save-view-state';
import { selectSavedViewState } from '@actions/ui';
import EmptyPeople from './EmptyPeople';
import styles from './People.module.scss';
import DelayedLoader from '@components/DelayedLoader';
import { GroupMembershipCount } from '@views/Group/Header';
import { useHotkeyHandler } from '@support/hooks/use-hotkey-handler';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { selectArchive } from '@actions/archive';

const viewID: ViewID = 'People';

export const PeopleHeader = () => {
  const navigate = useNavigate();
  const allPeople = useSelector(selectAdHocSearch('allPeopleSearch'));
  const archive = useSelector(selectArchive);
  const loc = useLocation();
  const isOnMap = loc.pathname.includes('map');
  const isOnPeople = !isOnMap && loc.pathname.includes('people');
  const isOnArchive = loc.pathname.includes('archive');

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.defaultPrevented) {
          return;
        }

        if (e.key === '1' && !isOnPeople) {
          navigate('/people');
        } else if (e.key === '2' && !isOnMap) {
          navigate('/people/map');
        } else if (e.key === '3' && !isOnArchive) {
          navigate('/archive');
        }
      },
    },
    [isOnPeople, navigate],
  );

  return (
    <div className={styles.viewHeader}>
      <div className={cn(styles.tab, styles.groupName, isOnPeople && styles.active)}>
        <KeyboardShortcut
          placement="bottom"
          shortcut={<KeyboardShortcut.Key>1</KeyboardShortcut.Key>}
        >
          <Link to={'/people'} className={styles.tabTitle}>
            People
          </Link>
        </KeyboardShortcut>
      </div>
      <div className={cn(styles.tab, styles.groupName, isOnMap && styles.active)}>
        <KeyboardShortcut
          placement="bottom"
          shortcut={<KeyboardShortcut.Key>2</KeyboardShortcut.Key>}
        >
          <Link to={'/people/map'} className={styles.tabTitle}>
            Map
          </Link>
        </KeyboardShortcut>
      </div>
      <div className={cn(styles.tab, isOnArchive && styles.active)}>
        <KeyboardShortcut
          placement="bottom"
          shortcut={<KeyboardShortcut.Key>3</KeyboardShortcut.Key>}
        >
          <Link to={'/archive'} className={cn(styles.tabTitle)}>
            Archive
          </Link>
        </KeyboardShortcut>
      </div>
      {isOnPeople && !allPeople.results.updated && <DelayedLoader loading />}
      {isOnArchive && !archive.updated && <DelayedLoader loading />}
    </div>
  );
};

export default function People() {
  const dispatch = useDispatch();
  const savedViewState = useSelector(selectSavedViewState(viewID));
  const [highlightedItem, setHighlightedItem] = useState(savedViewState?.selectedContact);
  const { setTerm, fetchMore, refetchCurrentPage } = allPeopleSearchActions;
  const allPeople = useSelector(selectAdHocSearch('allPeopleSearch'));
  const scrollingContainerRef = useRef(null);
  const viewScrolledAtMount = useScrollRestoration({ viewID, scrollingContainerRef });
  const loadNextPage = useCallback(() => dispatch(fetchMore()), []);
  const defaultHighlightedItem = useMemo(() => highlightedItem, []);

  useSaveViewState(viewID, { selectedContact: highlightedItem }, [highlightedItem]);
  useAppContactSelecting({ contact: highlightedItem, source: 'people' });
  useEffect(() => {
    if (allPeople.results.page > 1 && viewScrolledAtMount) {
      dispatch(refetchCurrentPage());
    } else {
      dispatch(setTerm('all', 100, []));
    }
  }, []);

  return (
    <ResultsDisplay
      search={allPeople}
      setHighlightedItem={setHighlightedItem}
      highlightedItem={highlightedItem}
      loadNextPage={loadNextPage}
      ref={scrollingContainerRef}
      title={<PeopleHeader />}
      defaultHighlightedItem={defaultHighlightedItem}
      defaultHighlightedIdx={1}
      emptyStateUI={<EmptyPeople />}
      listHeader={
        allPeople.results.totalResults > 0 ? (
          <GroupMembershipCount count={allPeople.results.totalResults} />
        ) : undefined
      }
    />
  );
}
